import { z } from "zod";

/**
 * Canonical map JSON, schema version 1 (brief section 7.1).
 *
 * The shape is the reference tool's export format. Numeric inputs are a finite number or "" (not
 * entered). The distinction between "" and 0 matters: an empty low or high case falls back to the
 * expected uplift, and an empty ramp means 0 while a missing ramp means 100. `importMap` turns any
 * reference export into this canonical form without changing a calculated value.
 */

export const SCHEMA_VERSION = 1;

export const LIMITS = {
  id: 40,
  title: 255,
  label: 255,
  shortText: 255,
  facilitator: 120,
  longText: 32_000,
  goals: 50,
  blocksPerGoal: 50,
  customLevers: 20,
  customFactors: 20,
  /** Largest magnitude accepted for any numeric input. Salesforce Number(18,4) holds 14 digits. */
  maxNumber: 1e12,
  /**
   * Largest stored map: the serialised canonical JSON must fit the Salesforce Long Text field
   * `Value_Map__c.Map_JSON__c` (131,072 characters). JavaScript string length counts UTF-16 code
   * units, which is never fewer than Salesforce's character count, so the check is conservative.
   */
  mapJsonChars: 131_072,
} as const;

export const IdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{1,40}$/, "IDs are 1 to 40 letters, digits, hyphens or underscores");

export const NumericInputSchema = z.union([
  z.number().min(-LIMITS.maxNumber).max(LIMITS.maxNumber),
  z.literal(""),
]);
export type NumericInput = z.infer<typeof NumericInputSchema>;

const label = z.string().max(LIMITS.label);
const longText = z.string().max(LIMITS.longText);

export const RevenueBlockSchema = z.object({
  id: IdSchema,
  type: z.literal("revenue"),
  label,
  method: z.enum(["percent", "drivers"]),
  baseline: NumericInputSchema,
  upliftPct: NumericInputSchema,
  upliftPctLow: NumericInputSchema,
  upliftPctHigh: NumericInputSchema,
  donors: NumericInputSchema,
  avgGift: NumericInputSchema,
  extraDonors: NumericInputSchema,
  avgGiftIncreasePct: NumericInputSchema,
  ramp: NumericInputSchema,
  growthPct: NumericInputSchema,
});

export const EfficiencyBlockSchema = z.object({
  id: IdSchema,
  type: z.literal("efficiency"),
  label,
  hoursPerWeekEach: NumericInputSchema,
  staff: NumericInputSchema,
  savingPct: NumericInputSchema,
  weeksPerYear: NumericInputSchema,
  hourlyCost: NumericInputSchema,
  growthPct: NumericInputSchema,
});

export const CostBlockSchema = z.object({
  id: IdSchema,
  type: z.literal("cost"),
  label,
  annualAmount: NumericInputSchema,
  likelihoodPct: NumericInputSchema,
  growthPct: NumericInputSchema,
});

export const BlockSchema = z.discriminatedUnion("type", [
  RevenueBlockSchema,
  EfficiencyBlockSchema,
  CostBlockSchema,
]);

export type RevenueBlock = z.infer<typeof RevenueBlockSchema>;
export type EfficiencyBlock = z.infer<typeof EfficiencyBlockSchema>;
export type CostBlock = z.infer<typeof CostBlockSchema>;
export type Block = z.infer<typeof BlockSchema>;
export type BlockType = Block["type"];

export const BLOCK_TYPES = [
  "revenue",
  "efficiency",
  "cost",
] as const satisfies readonly BlockType[];

export const LeverSchema = z.object({
  on: z.boolean(),
  measure: z.string().max(LIMITS.shortText),
});

export const CustomLeverSchema = z.object({
  id: IdSchema,
  name: z.string().max(LIMITS.shortText),
  measure: z.string().max(LIMITS.shortText),
});

export const ComplexityValueSchema = z.number().int().min(1).max(5);

export const CustomFactorSchema = z.object({
  id: IdSchema,
  label: z.string().max(LIMITS.shortText),
  value: ComplexityValueSchema,
});

export const ComplexitySchema = z.object({
  scale: ComplexityValueSchema,
  skills: ComplexityValueSchema,
  dependencies: ComplexityValueSchema,
  change: ComplexityValueSchema,
  custom: z.array(CustomFactorSchema).max(LIMITS.customFactors),
  dependsOn: z.union([IdSchema, z.literal("")]),
});

export const GoalSchema = z.object({
  id: IdSchema,
  title: z.string().max(LIMITS.title),
  stakeholder: longText,
  personalWin: longText,
  rightNow: longText,
  objective: longText,
  capability: longText,
  outcome: longText,
  costInaction: longText,
  levers: z.object({
    fund: LeverSchema,
    eff: LeverSchema,
    exp: LeverSchema,
    risk: LeverSchema,
  }),
  custom: z.array(CustomLeverSchema).max(LIMITS.customLevers),
  vcase: z.object({
    blocks: z.array(BlockSchema).max(LIMITS.blocksPerGoal),
  }),
  complexity: ComplexitySchema,
});

export type Lever = z.infer<typeof LeverSchema>;
export type CustomLever = z.infer<typeof CustomLeverSchema>;
export type CustomFactor = z.infer<typeof CustomFactorSchema>;
export type Complexity = z.infer<typeof ComplexitySchema>;
export type Goal = z.infer<typeof GoalSchema>;

export const OrgSchema = z.object({
  name: z.string().max(LIMITS.shortText),
  facilitator: z.string().max(LIMITS.facilitator),
  date: z.union([z.literal(""), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Dates are YYYY-MM-DD")]),
});

export const MetaSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  mapId: IdSchema.optional(),
  revision: z.number().int().min(0).optional(),
});

export const ValueMapSchema = z
  .object({
    org: OrgSchema,
    horizon: z.number().int().min(1).max(5),
    goals: z.array(GoalSchema).max(LIMITS.goals),
    meta: MetaSchema.optional(),
  })
  .superRefine((map, ctx) => {
    const goalIds = new Set<string>();
    map.goals.forEach((goal, gi) => {
      if (goalIds.has(goal.id)) {
        ctx.addIssue({ code: "custom", message: "Duplicate goal ID", path: ["goals", gi, "id"] });
      }
      goalIds.add(goal.id);
      const blockIds = new Set<string>();
      goal.vcase.blocks.forEach((block, bi) => {
        if (blockIds.has(block.id)) {
          ctx.addIssue({
            code: "custom",
            message: "Duplicate block ID",
            path: ["goals", gi, "vcase", "blocks", bi, "id"],
          });
        }
        blockIds.add(block.id);
      });
    });
  });

export type Org = z.infer<typeof OrgSchema>;
export type Meta = z.infer<typeof MetaSchema>;
export type ValueMap = z.infer<typeof ValueMapSchema>;
